import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatUSD, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, RefreshCw, AlertTriangle, PlayCircle } from "lucide-react";

const LOW_BALANCE_THRESHOLD = 20;

export default function ProviderPanel() {
  const [status, setStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [refreshingBalance, setRefreshingBalance] = useState(false);

  const loadStatus = async () => {
    try {
      const [{ data: s }, { data: b }] = await Promise.all([
        api.get("/admin/dingconnect/status"),
        api.get("/admin/dingconnect/balance"),
      ]);
      setStatus(s);
      setBalance(b);
    } catch (e) {
      // master-only — silently ignore
    }
  };

  // Initial load only — NO auto-polling. The balance endpoint is backed by a
  // 15-minute server-side cache; the manual "Refresh balance" button is the
  // only path that re-reads upstream on demand (rate-limit safe, Sep 2026).
  useEffect(() => {
    loadStatus();
  }, []);

  const refreshBalance = async () => {
    if (refreshingBalance) return;
    setRefreshingBalance(true);
    try {
      await api.post("/admin/providers/dingconnect/healthcheck");
      await loadStatus();
      toast.success("Balance refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Balance refresh failed");
    } finally {
      setRefreshingBalance(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    const t = toast.loading("Syncing catalog from Provider…");
    try {
      const { data } = await api.post("/admin/dingconnect/sync-catalog");
      toast.success(
        `Catalog synced: ${data.countries} countries · ${data.operators} operators · ${data.products} products`,
        { id: t }
      );
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async () => {
    setReconciling(true);
    const t = toast.loading("Reconciling pending topups…");
    try {
      const { data } = await api.post("/admin/dingconnect/reconcile");
      toast.success(
        data.updated ? `Updated ${data.updated} topup(s)` : "All topups up-to-date",
        { id: t }
      );
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed", { id: t });
    } finally {
      setReconciling(false);
    }
  };

  if (!status) return null;

  const dcBalance = balance ? Number(balance.balance || 0) : null;
  const isLow = dcBalance != null && dcBalance < LOW_BALANCE_THRESHOLD;

  return (
    <div className="space-y-4">
      {isLow && (
        <div
          data-testid="dingconnect-low-balance-banner"
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" strokeWidth={2.2} />
          <div className="text-sm">
            <div className="font-semibold">Low Provider balance.</div>
            <div className="text-amber-800">
              Top up your provider account before sending real topups. Current balance:{" "}
              <span className="font-semibold">{formatUSD(dcBalance)} {balance?.currency || "USD"}</span>.
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Catalog from Provider
              <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest " + (status.is_real ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
                <span className={"w-1.5 h-1.5 rounded-full " + (status.is_real ? "bg-emerald-500" : "bg-amber-500")} />
                {status.is_real ? "Live API" : "Mock"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {status.api_base?.replace(/^https?:\/\//, "") || "n/a"} ·{" "}
              {status.last_sync
                ? `last synced ${formatDate(status.last_sync.finished_at)}`
                : "never synced"}
              {" · "}auto-reconcile every 30s
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="dingconnect-reconcile-btn"
              onClick={reconcile}
              disabled={reconciling}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Reconcile now
            </button>
            <button
              data-testid="dingconnect-sync-btn"
              onClick={sync}
              disabled={busy}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[#0F62FE] text-[#0F62FE] text-sm font-semibold hover:bg-[#EDF5FF] disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync catalog
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="dingconnect-balance-row">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Provider balance
            </div>
            <div className="mt-1 text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="dingconnect-balance-value">
              {dcBalance != null ? `${formatUSD(dcBalance)} ${balance?.currency || "USD"}` : "—"}
              {balance?.stale && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200"
                  data-testid="dingconnect-balance-stale-badge"
                  title="Last known value — upstream rate-limited (HTTP 429). Refreshes automatically every 15 min."
                >
                  cached
                </span>
              )}
            </div>
            {balance?.checked_at && (
              <div className="text-[10px] text-slate-500 mt-0.5" data-testid="dingconnect-balance-checked-at">
                checked {formatDate(balance.checked_at)}
              </div>
            )}
          </div>
          <button
            data-testid="dingconnect-refresh-balance-btn"
            onClick={refreshBalance}
            disabled={refreshingBalance}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-white disabled:opacity-50"
          >
            {refreshingBalance ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh balance
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat label="Countries" value={status.counts?.countries ?? 0} testid="dc-stat-countries" />
          <Stat label="Operators" value={status.counts?.operators ?? 0} testid="dc-stat-operators" />
          <Stat label="Products" value={status.counts?.products ?? 0} testid="dc-stat-products" />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div
        className="mt-1 text-lg font-bold text-slate-900"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {value}
      </div>
    </div>
  );
}
